import { Children, type ReactNode, useId } from 'react'
import { PageSection } from '@/features/jobs/details/components/PageLayout'
import { SectionHeadline } from '@/components/SectionHeadline'
import type { JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import { observer } from 'mobx-react-lite'
import { Button } from '@/components/Button'
import {
  Card,
  CardContent,
  CardContentDecoration,
  CardContentFooterActions,
  CardSubtitle,
  CardTitle,
} from '@/components/Card'
import {
  DocumentCardItem,
  DocumentCardItemSkeleton,
  DocumentCardList,
} from './DocumentCards'
import { Icon } from '@/components/Icon'
import { StyledLink } from '@/components/Link'
import { Dropdown, DropdownItem } from '@/components/Dropdown'
import { useFileTrigger } from '@/hooks/file-hooks'
import { SkeletonListInfiniteScrollTrigger } from '@/components/SkeletonListInfiniteScrollTrigger'
import { DocumentThumbnail } from '@/features/docs/components/DocumentThumbnail'
import { DocumentActionsDropdownContainer } from '@/features/docs/components/DocumentActionsDropdownContainer'
import type { PendingDocumentViewModel } from '@/features/docs/DocumentUploaderViewModel'
import type { DocumentViewModel } from '@/features/docs/DocumentViewModel'
import type { SubmittedJob } from '@/features/jobs/details/SubmittedJob'
import { match } from '@/utils/pattern-match-util'

export interface DocumentsSectionProps {
  /**
   * Must be either {@link DocumentCardItem}s or {@link SkeletonList} with a
   * {@link DocumentCardItem} as the skeleton.
   */
  children?: ReactNode
  /**
   * Called when the user wants to upload a new document.
   */
  onUploadNew?: (files: File[]) => unknown
  /**
   * Called when the user wants to link an existing document.
   */
  onLinkExisting?: () => unknown
  /**
   * Test ID attribute.
   */
  testId?: string
  /**
   * Test ID attribute for the add new dropdown trigger button.
   */
  dropdownButtonTestId?: string
  /**
   * Test ID attribute for the upload new dropdown item.
   */
  uploadNewItemTestId?: string
  /**
   * Test ID attribute for the link existing dropdown item.
   */
  linkExistingItemTestId?: string
  /**
   * Test ID attribute for the empty state card.
   */
  emptyCardTestId?: string
  /**
   * Test ID attribute for the empty state upload new link.
   */
  emptyUploadNewLinkTestId?: string
  /**
   * Test ID attribute for the empty state link existing link.
   */
  emptyLinkExistingLinkTestId?: string
}

/**
 * Displays documents for a job.
 */
export function DocumentsSection({
  children,
  onUploadNew,
  onLinkExisting,
  testId,
  dropdownButtonTestId,
  uploadNewItemTestId,
  linkExistingItemTestId,
  emptyCardTestId,
  emptyUploadNewLinkTestId,
  emptyLinkExistingLinkTestId,
}: DocumentsSectionProps) {
  const headlineId = useId()

  const isEmpty =
    Children.count(Children.toArray(children).filter(Boolean)) === 0
  const onUploadPress = useFileTrigger({
    allowMultiple: true,
    onFilesSelected: onUploadNew ?? (() => void 0),
  })

  return (
    <PageSection testId={testId}>
      <SectionHeadline
        id={headlineId}
        action={
          Boolean(onUploadNew ?? onLinkExisting) && (
            <Dropdown
              title="Add file"
              trigger={
                <Button
                  variant="text"
                  iconLeft="add"
                  label="Add new"
                  testId={dropdownButtonTestId}
                />
              }
            >
              {onUploadNew && (
                <DropdownItem
                  icon="upload"
                  onAction={onUploadPress}
                  testId={uploadNewItemTestId}
                >
                  Upload new
                </DropdownItem>
              )}
              {onLinkExisting && (
                <DropdownItem
                  icon="link"
                  onAction={onLinkExisting}
                  testId={linkExistingItemTestId}
                >
                  Link existing
                </DropdownItem>
              )}
            </Dropdown>
          )
        }
      >
        Your documents
      </SectionHeadline>
      {isEmpty ? (
        <Card testId={emptyCardTestId}>
          <CardContent>
            <CardContentDecoration>
              <Icon name="image" variant="surface-background" />
            </CardContentDecoration>
            <CardTitle>No files yet</CardTitle>
            <CardSubtitle variant="base">
              Any file that is uploaded to this request will appear here.
            </CardSubtitle>
            {Boolean(onUploadNew ?? onLinkExisting) && (
              <CardContentFooterActions>
                {onUploadNew && (
                  <StyledLink
                    onPress={onUploadPress}
                    testId={emptyUploadNewLinkTestId}
                  >
                    Upload new
                  </StyledLink>
                )}

                {onLinkExisting && (
                  <StyledLink
                    onPress={onLinkExisting}
                    testId={emptyLinkExistingLinkTestId}
                  >
                    Link existing
                  </StyledLink>
                )}
              </CardContentFooterActions>
            )}
          </CardContent>
        </Card>
      ) : (
        <DocumentCardList aria-labelledby={headlineId}>
          {children}
        </DocumentCardList>
      )}
    </PageSection>
  )
}

interface DocumentsSectionContainerProps {
  vm: JobDetailsViewModel
}

/**
 * Documents Section Container
 */
export const DocumentsSectionContainer = observer(
  function DocumentsSectionContainer({ vm }: DocumentsSectionContainerProps) {
    const documentsListVm = vm.docsVm.documentsListVm
    const isShowingEmptyState =
      Boolean(documentsListVm.emptyState) &&
      documentsListVm.uploaderVm.pendingDocuments.length === 0

    return (
      <DocumentsSection
        testId="documents-section"
        dropdownButtonTestId="documents-section-add-new-button"
        linkExistingItemTestId="documents-section-link-existing-item"
        uploadNewItemTestId="documents-section-upload-new-item"
        emptyCardTestId="documents-section-empty-card"
        emptyLinkExistingLinkTestId="documents-section-empty-link-existing-link"
        emptyUploadNewLinkTestId="documents-section-empty-upload-new-link"
        onUploadNew={
          vm.job.status === 'Closed'
            ? undefined
            : documentsListVm.uploaderVm.upload
        }
        onLinkExisting={
          vm.job.status === 'Closed'
            ? undefined
            : vm.docsVm.linkDocumentsVm.show
        }
      >
        {isShowingEmptyState ? null : (
          <>
            {documentsListVm.uploaderVm.pendingDocuments.map((pendingVm) => (
              <PendingDocumentCardItemContainer
                key={pendingVm.cid}
                pendingVm={pendingVm}
              />
            ))}
            {documentsListVm.allDocuments.map((docVm) => (
              <DocumentCardItemContainer
                key={docVm.doc.id}
                docVm={docVm}
                job={vm.job}
              />
            ))}
            <SkeletonListInfiniteScrollTrigger
              scroller={documentsListVm.scroller}
              maxSkeletons={6}
            >
              <DocumentCardItemSkeleton />
            </SkeletonListInfiniteScrollTrigger>
          </>
        )}
      </DocumentsSection>
    )
  },
)

interface DocumentCardItemContainerProps {
  docVm: DocumentViewModel
  job: SubmittedJob
}

/**
 * Document Card Item Container
 */
export const DocumentCardItemContainer = observer(
  function DocumentCardItemContainer({
    docVm,
    job,
  }: DocumentCardItemContainerProps) {
    const access = docVm.doc.accesses.find((a) =>
      match(a.accessType, 'type', {
        Job: (jobAccess) => jobAccess.jobId === job.id,
        Unknown: () => false,
      }),
    )

    return (
      <DocumentCardItem
        title={docVm.doc.title}
        progress={docVm.doc.isProcessing ? 'indefinite' : undefined}
        description={docVm.doc.typeName ?? undefined}
        extras={<DocumentActionsDropdownContainer access={access} vm={docVm} />}
        thumbnail={
          <DocumentThumbnail
            imageUrl={docVm.doc.typeImageUrl}
            name={docVm.doc.title}
            isSkeleton={docVm.fetchType.pending}
          />
        }
        testId={`document-list-item-${docVm.doc.id}`}
        titleTestId={`document-list-item-title-${docVm.doc.title}`}
      />
    )
  },
)

interface PendingDocumentCardItemContainerProps {
  pendingVm: PendingDocumentViewModel
}

/**
 * Pending Document Card Item Container
 */
export const PendingDocumentCardItemContainer = observer(
  function PendingDocumentCardItemContainer({
    pendingVm,
  }: PendingDocumentCardItemContainerProps) {
    return (
      <DocumentCardItem
        title={pendingVm.fileName}
        progress={pendingVm.progressPercent}
        description={pendingVm.type?.name}
        thumbnail={<DocumentThumbnail isSkeleton />}
      />
    )
  },
)
